import React, { useState, useEffect } from 'react';
import { ArrowRightLeft, Package, AlertCircle, CheckCircle } from 'lucide-react';
import { Button, Loading, ErrorMessage } from '@/components/common';
import { transferToDriver } from '@/api/transactions';
import { batteriesAPI } from '@/api/batteries';
import { getAllDrivers } from '@/api/drivers';
import { suppliersAPI } from '@/api/suppliers';
import type { BatterySummary, Driver, Supplier } from '@/types';

export const TransferPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [batteries, setBatteries] = useState<BatterySummary[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  
  const [selectedBattery, setSelectedBattery] = useState<BatterySummary | null>(null);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [batteriesRes, driversRes, suppliersRes] = await Promise.all([
        batteriesAPI.getAll({ limit: 1000 }),
        getAllDrivers(),
        suppliersAPI.getAll()
      ]);

      setBatteries(batteriesRes.data.data.batteries || []);
      setDrivers(driversRes.data.drivers || []);
      setSuppliers(suppliersRes.data.data.suppliers || []);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'فشل في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async () => {
    if (!selectedBattery || !selectedSupplier || !selectedDriver || quantity <= 0) {
      setMessage({ type: 'error', text: 'الرجاء اختيار جميع الحقول المطلوبة' });
      return;
    }

    const availableStock = selectedBattery.warehouseStock[selectedSupplier.code] || 0;
    
    if (availableStock < quantity) {
      setMessage({ 
        type: 'error', 
        text: `المخزون غير كافٍ. المتاح: ${availableStock}` 
      });
      return;
    }

    try {
      setTransferring(true);
      setMessage(null);

      await transferToDriver({
        batteryId: selectedBattery.id,
        supplierId: selectedSupplier.id,
        driverId: selectedDriver.id,
        quantity,
        notes
      });

      setMessage({ 
        type: 'success', 
        text: `تم نقل ${quantity} بطارية إلى ${selectedDriver.arabicName} بنجاح` 
      });

      // Reset form
      setSelectedBattery(null);
      setSelectedSupplier(null);
      setSelectedDriver(null);
      setQuantity(1);
      setNotes('');

      // Reload data to update stock
      await loadData();
    } catch (err: any) {
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.error?.message || 'فشل في نقل البطاريات' 
      });
    } finally {
      setTransferring(false);
    }
  };

  const getAvailableSuppliersForBattery = (battery: BatterySummary): Supplier[] => {
    return suppliers.filter(supplier => {
      const stock = battery.warehouseStock[supplier.code];
      return stock && stock > 0;
    });
  };

  if (loading) return <Loading text="جاري تحميل البيانات..." />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <ArrowRightLeft className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold text-primary">نقل بطاريات للسائق</h1>
          <p className="text-gray-400 mt-1">انقل البطاريات من المستودع إلى السائقين</p>
        </div>
      </div>

      {/* Transfer Form */}
      <div className="card">
        <h2 className="text-xl font-bold text-primary mb-4">تفاصيل النقل</h2>

        {message && (
          <div className={`mb-4 p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-900/50 text-green-300' : 'bg-red-900/50 text-red-300'
          }`}>
            <div className="flex items-center gap-2">
              {message.type === 'success' ? (
                <CheckCircle className="h-5 w-5" />
              ) : (
                <AlertCircle className="h-5 w-5" />
              )}
              <span>{message.text}</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Battery Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              البطارية *
            </label>
            <select
              className="input w-full"
              value={selectedBattery?.id || ''}
              onChange={(e) => {
                const battery = batteries.find(b => b.id === parseInt(e.target.value));
                setSelectedBattery(battery || null);
                setSelectedSupplier(null);
              }}
            >
              <option value="">اختر البطارية</option>
              {batteries
                .filter(b => b.warehouseStock.total > 0)
                .map(battery => (
                  <option key={battery.id} value={battery.id}>
                    {battery.brand} - {battery.size} 
                    {battery.tradeName && ` (${battery.tradeName})`}
                    {` - مخزون: ${battery.warehouseStock.total}`}
                  </option>
                ))}
            </select>
          </div>

          {/* Supplier Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              المورد *
            </label>
            <select
              className="input w-full"
              value={selectedSupplier?.id || ''}
              onChange={(e) => {
                const supplier = suppliers.find(s => s.id === parseInt(e.target.value));
                setSelectedSupplier(supplier || null);
              }}
              disabled={!selectedBattery}
            >
              <option value="">اختر المورد</option>
              {selectedBattery && getAvailableSuppliersForBattery(selectedBattery).map(supplier => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.code} - متاح: {selectedBattery.warehouseStock[supplier.code]}
                </option>
              ))}
            </select>
          </div>

          {/* Driver Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              السائق *
            </label>
            <select
              className="input w-full"
              value={selectedDriver?.id || ''}
              onChange={(e) => {
                const driver = drivers.find(d => d.id === parseInt(e.target.value));
                setSelectedDriver(driver || null);
              }}
            >
              <option value="">اختر السائق</option>
              {drivers
                .filter(d => d.isActive)
                .map(driver => (
                  <option key={driver.id} value={driver.id}>
                    {driver.arabicName} ({driver.englishName})
                  </option>
                ))}
            </select>
          </div>

          {/* Quantity */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              الكمية *
            </label>
            <input
              type="number"
              min="1"
              max={selectedBattery && selectedSupplier ? (selectedBattery.warehouseStock[selectedSupplier.code] || 0) : 0}
              className="input w-full"
              value={quantity}
              onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
              disabled={!selectedBattery || !selectedSupplier}
            />
            {selectedBattery && selectedSupplier && (
              <p className="text-sm text-gray-400 mt-1">
                الحد الأقصى: {selectedBattery.warehouseStock[selectedSupplier.code] || 0}
              </p>
            )}
          </div>

          {/* Notes */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              ملاحظات
            </label>
            <textarea
              className="input w-full"
              rows={3}
              placeholder="أضف ملاحظات إضافية (اختياري)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>

        {/* Transfer Summary */}
        {selectedBattery && selectedSupplier && selectedDriver && (
          <div className="mt-6 p-4 bg-dark-lighter rounded-lg border border-primary/20">
            <h3 className="text-sm font-semibold text-primary mb-3">ملخص النقل</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-400">البطارية</p>
                <p className="text-white font-medium">{selectedBattery.brand} {selectedBattery.size}</p>
              </div>
              <div>
                <p className="text-gray-400">المورد</p>
                <p className="text-white font-medium">{selectedSupplier.code}</p>
              </div>
              <div>
                <p className="text-gray-400">السائق</p>
                <p className="text-white font-medium">{selectedDriver.arabicName}</p>
              </div>
              <div>
                <p className="text-gray-400">الكمية</p>
                <p className="text-white font-medium">{quantity} قطعة</p>
              </div>
            </div>
          </div>
        )}

        {/* Action Button */}
        <div className="mt-6 flex gap-3">
          <Button
            onClick={handleTransfer}
            disabled={!selectedBattery || !selectedSupplier || !selectedDriver || transferring || quantity <= 0}
            className="gap-2"
          >
            <ArrowRightLeft className="h-5 w-5" />
            {transferring ? 'جاري النقل...' : 'نقل إلى السائق'}
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card bg-gradient-to-br from-blue-600 to-blue-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-200 text-sm">البطاريات المتاحة</p>
              <p className="text-3xl font-bold text-white mt-1">
                {batteries.filter(b => b.warehouseStock.total > 0).length}
              </p>
            </div>
            <Package className="h-12 w-12 text-blue-300 opacity-50" />
          </div>
        </div>

        <div className="card bg-gradient-to-br from-green-600 to-green-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-200 text-sm">السائقون النشطون</p>
              <p className="text-3xl font-bold text-white mt-1">
                {drivers.filter(d => d.isActive).length}
              </p>
            </div>
            <ArrowRightLeft className="h-12 w-12 text-green-300 opacity-50" />
          </div>
        </div>

        <div className="card bg-gradient-to-br from-purple-600 to-purple-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-200 text-sm">إجمالي المخزون</p>
              <p className="text-3xl font-bold text-white mt-1">
                {batteries.reduce((sum, b) => sum + (b.warehouseStock.total || 0), 0)}
              </p>
            </div>
            <Package className="h-12 w-12 text-purple-300 opacity-50" />
          </div>
        </div>
      </div>
    </div>
  );
};
